// ─── useHistory ────────────────────────────────────────────────────────────────
// History management: delete, confirm output, inline editing, content memory.

import { useCallback } from 'react';
import { getFullOutputTextForEntry, getBlogHtmlForEntry } from '../utils/formatters.js';
import { sanitizeHtml } from '../utils/textProcessing.js';

export function useHistory({ state, dispatch, contentMemoryRef }) {

  const deleteEntry = useCallback((entryId) => {
    dispatch({ type: 'DELETE_HISTORY_ENTRY', entryId });
    contentMemoryRef.current = contentMemoryRef.current.filter(m => m.id !== entryId);
  }, [dispatch, contentMemoryRef]);

  const confirmOutput = useCallback((entry) => {
    dispatch({ type: 'SET_BATCH', payload: { confirmedOutput: entry, wizardStep: 3 } });
  }, [dispatch]);

  const getOutputText = useCallback((entry) => getFullOutputTextForEntry(entry), []);

  const getBlogHtml = useCallback((entry) => getBlogHtmlForEntry(entry, sanitizeHtml), []);

  const handleEditSave = useCallback((entryId) => {
    const { editedContent } = state;
    const fields = editedContent[entryId] || {};
    if (!Object.keys(fields).length) {
      dispatch({ type: 'SET_KEY', key: 'editingEntryId', value: null });
      return;
    }

    // Apply field edits to the history entry
    dispatch({ type: 'SET_KEY', key: 'outputHistory', value: prev =>
      (prev || []).map(e => {
        if (e.id !== entryId) return e;
        const updatedResults = applyFieldEdits({ ...e.results }, fields);
        const validationStatus = {};
        Object.keys(fields).forEach(k => { validationStatus[k] = 'edited'; });
        dispatch({ type: 'SET_KEY', key: 'editValidationStatus', value: prev2 => ({ ...prev2, [entryId]: { ...(prev2[entryId] || {}), ...validationStatus } }) });
        return { ...e, results: updatedResults };
      })
    });
    dispatch({ type: 'SET_KEY', key: 'editingEntryId', value: null });
  }, [dispatch, state]);

  const handleEditCancel = useCallback((entryId) => {
    dispatch({ type: 'SET_BATCH', payload: {
      editingEntryId: null,
      editedContent: { ...state.editedContent, [entryId]: {} },
    }});
  }, [dispatch, state.editedContent]);

  const toggleEditMode = useCallback((entryId) => {
    const isCurrentlyEditing = state.editingEntryId === entryId;
    if (isCurrentlyEditing) {
      handleEditSave(entryId);
    } else {
      dispatch({ type: 'SET_KEY', key: 'editingEntryId', value: entryId });
    }
  }, [state.editingEntryId, handleEditSave, dispatch]);

  const exportEntry = useCallback((entry) => {
    const text = getFullOutputTextForEntry(entry);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entry.mode || 'output'}_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const downloadAs = useCallback((entry, format) => {
    const filename = `${entry.modeConfig?.name || entry.mode || 'output'}_${new Date(entry.timestamp).toISOString().slice(0,10)}`;
    const text = getFullOutputTextForEntry(entry);
    const meta = buildMetaBlock(entry);
    const inputMd      = buildInputSummaryMd(entry.params);
    const inputJsonTag = buildInputJsonComment(entry);

    if (format === 'md') {
      const md = `${meta}\n${inputMd}\n${inputJsonTag}\n\n---\n\n${text}`;
      downloadBlob(new Blob([md], { type: 'text/markdown;charset=utf-8' }), `${filename}.md`);
    } else if (format === 'docx') {
      const html = buildHtmlDocument(entry, meta, text);
      const blob = new Blob(
        ['\ufeff' + html],
        { type: 'application/vnd.ms-word;charset=utf-8' }
      );
      downloadBlob(blob, `${filename}.doc`);
    } else if (format === 'pdf') {
      const html = buildHtmlDocument(entry, meta, text);
      // Render inside a hidden iframe so html2canvas never touches the main page
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:700px;height:900px;border:none;visibility:hidden;';
      document.body.appendChild(iframe);
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();
      import('html2pdf.js').then(({ default: html2pdf }) => {
        html2pdf()
          .set({
            margin: [12, 12, 12, 12],
            filename: `${filename}.pdf`,
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          })
          .from(iframeDoc.body)
          .save()
          .then(() => document.body.removeChild(iframe))
          .catch(() => document.body.removeChild(iframe));
      });
    }
  }, []);

  return {
    deleteEntry,
    confirmOutput,
    getOutputText,
    getBlogHtml,
    handleEditSave,
    handleEditCancel,
    toggleEditMode,
    exportEntry,
    downloadAs,
  };
}

// ─── Download Helpers ─────────────────────────────────────────────────────────

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function buildMetaBlock(entry) {
  const ts = new Date(entry.timestamp);
  const date = ts.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  const lines = [`# ${entry.modeConfig?.name || entry.mode || 'Output'}`, ''];
  lines.push(`**Data:** ${date}`);
  const r = entry.results || {};
  if (r.platform || entry.params?.platform)
    lines.push(`**Piattaforma:** ${r.platform || entry.params.platform}`);
  if (entry.params?.objective)
    lines.push(`**Obiettivo:** ${entry.params.objective}`);
  if (entry.params?.persona)
    lines.push(`**Persona:** ${entry.params.persona}`);
  if (r.topic)
    lines.push(`**Topic:** ${r.topic}`);
  if (r.factualConfidence != null)
    lines.push(`**Fact-check:** ${r.factualConfidence}/100`);
  if (r.styleScore != null)
    lines.push(`**Style score:** ${r.styleScore}/100`);
  return lines.join('\n');
}

function escapeHtml(str) {
  return (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function buildHtmlDocument(entry, meta, text) {
  const modeName = entry.modeConfig?.name || entry.mode || 'Output';
  const color = entry.modeConfig?.color || '#6366F1';
  const ts = new Date(entry.timestamp);
  const date = ts.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  const r = entry.results || {};

  let metaRows = `<tr><td style="color:#888;padding:4px 12px 4px 0;">Data</td><td>${date}</td></tr>`;
  if (r.platform || entry.params?.platform)
    metaRows += `<tr><td style="color:#888;padding:4px 12px 4px 0;">Piattaforma</td><td>${escapeHtml(r.platform || entry.params?.platform)}</td></tr>`;
  if (entry.params?.objective)
    metaRows += `<tr><td style="color:#888;padding:4px 12px 4px 0;">Obiettivo</td><td>${escapeHtml(entry.params.objective)}</td></tr>`;
  if (entry.params?.persona)
    metaRows += `<tr><td style="color:#888;padding:4px 12px 4px 0;">Persona</td><td>${escapeHtml(entry.params.persona)}</td></tr>`;
  if (r.topic)
    metaRows += `<tr><td style="color:#888;padding:4px 12px 4px 0;">Topic</td><td>${escapeHtml(r.topic)}</td></tr>`;

  const inputHtml    = buildInputSummaryHtml(entry.params, color);
  const inputJsonTag = buildInputJsonComment(entry);

  // Convert text to HTML paragraphs, preserving markdown headers
  const bodyHtml = escapeHtml(text)
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 style="font-size:20px;">$1</h1>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>');

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${escapeHtml(modeName)}</title>
<style>
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #222; line-height: 1.7; max-width: 700px; margin: 0 auto; padding: 32px 24px; }
  h1 { color: ${color}; font-size: 24px; margin-bottom: 4px; }
  h2 { color: ${color}; font-size: 18px; margin-top: 24px; }
  h3 { color: #444; font-size: 15px; margin-top: 18px; }
  table { font-size: 13px; margin-bottom: 20px; }
  hr { border: none; border-top: 2px solid ${color}22; margin: 20px 0; }
  p { margin: 8px 0; }
  .esc-input-block { font-size: 12px; background: #f7f7fb; border: 1px solid #e2e2ee; border-radius: 8px; padding: 12px 16px; margin: 16px 0; }
  .esc-input-block h3 { color: ${color}; margin: 0 0 8px 0; font-size: 13px; }
  .esc-input-block ul { margin: 0; padding-left: 18px; }
  .esc-input-block li { margin: 2px 0; }
</style></head><body>
${inputJsonTag}
<h1>${escapeHtml(modeName)}</h1>
<table>${metaRows}</table>
${inputHtml}
<hr/>
<p>${bodyHtml}</p>
</body></html>`;
}

// ─── Input Summary Helpers ────────────────────────────────────────────────────
// Render the wire-shape `entry.params` as a human-readable section + a hidden
// JSON marker that the file-load picker reads back to reverse-map state.

export function collectInputRows(params) {
  if (!params) return { rows: [], modeRows: [], refs: [], keyPoints: [] };
  const cp = params.content_params   || {};
  const rs = params.research_sources || {};
  const yn = (v) => v ? 'sì' : 'no';
  const rows = [];
  const push = (label, val) => {
    if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) return;
    rows.push({ label, value: String(val) });
  };

  push('Argomento',         params.additional_context);
  push('Modalità',          params.activeMode);
  push('Formato',           params.content_format);
  push('Piattaforma',       params.platform);
  push('Persona',           params.persona);
  push('Obiettivo',         params.objective);
  push('Tone',              params.tone_hint);
  push('Contesto e angolo', params.topic);
  push('CTA testo',         params.cta);
  push('CTA URL',           params.cta_url);

  const keyPoints = Array.isArray(params.key_points) ? params.key_points : [];
  const refs = Array.isArray(rs.request_urls) ? rs.request_urls.filter(Boolean) : [];

  push('KB attiva',                rs.use_company_website || rs.use_company_docs ? 'sì' : null);
  push('Query KB',                 rs.kb_search_query);
  push('Documenti caricati',       rs.use_uploaded_documents ? 'sì' : null);
  push('Query upload',             rs.upload_search_query);
  push('Ricerca web',              rs.use_web_search ? 'sì' : null);
  push('Query web',                rs.web_search_query);
  push('Risultati web',            rs.use_web_search ? rs.web_search_max_results : null);
  push('Channel social',           params.socialChannel && params.activeMode === 'social' ? params.socialChannel : null);
  push('Channel email',            params.emailChannel && params.activeMode === 'email' ? params.emailChannel : null);
  void yn;

  const modeRows = [];
  Object.keys(cp).forEach(k => {
    const v = cp[k];
    if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return;
    // Blog-SEO structured keywords: render as "primary; secondary (vol/mese); …"
    if (k === 'keywords' && v && typeof v === 'object' && !Array.isArray(v)) {
      const parts = [];
      if (v.primary) parts.push(v.primary);
      if (Array.isArray(v.secondary)) {
        v.secondary.filter(r => r && r.keyword).forEach(r => {
          parts.push(r.monthly_volume != null ? `${r.keyword} (${r.monthly_volume}/mese)` : r.keyword);
        });
      }
      if (parts.length) modeRows.push({ label: k, value: parts.join('; ') });
      return;
    }
    modeRows.push({ label: k, value: Array.isArray(v) ? v.join(', ') : String(v) });
  });

  return { rows, modeRows, refs, keyPoints };
}

function buildInputSummaryMd(params) {
  const { rows, modeRows, refs, keyPoints } = collectInputRows(params);
  if (!rows.length && !modeRows.length && !refs.length && !keyPoints.length) return '';

  const lines = ['', '## Input usato per generare questo output', ''];
  rows.forEach(({ label, value }) => lines.push(`- **${label}:** ${value}`));
  if (keyPoints.length) {
    lines.push('- **Punti chiave:**');
    keyPoints.forEach(kp => lines.push(`  - ${kp}`));
  }
  if (refs.length) {
    lines.push('- **URL di riferimento:**');
    refs.forEach(u => lines.push(`  - ${u}`));
  }
  if (modeRows.length) {
    lines.push('- **Parametri modalità:**');
    modeRows.forEach(({ label, value }) => lines.push(`  - ${label}: ${value}`));
  }
  return lines.join('\n');
}

function buildInputSummaryHtml(params, color) {
  const { rows, modeRows, refs, keyPoints } = collectInputRows(params);
  if (!rows.length && !modeRows.length && !refs.length && !keyPoints.length) return '';

  const liRow = ({ label, value }) =>
    `<li><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</li>`;
  const liNested = (label, items) =>
    `<li><strong>${escapeHtml(label)}:</strong><ul>${items.map(i => `<li>${escapeHtml(i)}</li>`).join('')}</ul></li>`;

  const main = rows.map(liRow).join('');
  const kp   = keyPoints.length  ? liNested('Punti chiave', keyPoints) : '';
  const r    = refs.length       ? liNested('URL di riferimento', refs) : '';
  const mp   = modeRows.length   ? liNested('Parametri modalità', modeRows.map(m => `${m.label}: ${m.value}`)) : '';
  void color;
  return `<div class="esc-input-block"><h3>Input usato per generare questo output</h3><ul>${main}${kp}${r}${mp}</ul></div>`;
}

function buildInputJsonComment(entry) {
  const data = {
    version: 1,
    entryId: entry.id,
    timestamp: entry.timestamp,
    mode: entry.mode,
    params: entry.params,
  };
  return `<!-- ESC_INPUT_DATA_V1: ${JSON.stringify(data)} -->`;
}

// ─── Edit Helpers ─────────────────────────────────────────────────────────────

function applyFieldEdits(results, fields) {
  const updated = { ...results };
  for (const [path, value] of Object.entries(fields)) {
    const parts = path.split('.');
    if (parts.length === 1) {
      updated[parts[0]] = value;
    } else if (parts.length === 3 && parts[0] === 'slides') {
      const idx = parseInt(parts[1], 10);
      const field = parts[2];
      if (Array.isArray(updated.slides)) {
        updated.slides = updated.slides.map((s, i) => i === idx ? { ...s, [field]: value } : s);
      }
    } else if (parts.length === 3 && parts[0] === 'sections') {
      const idx = parseInt(parts[1], 10);
      const field = parts[2];
      if (Array.isArray(updated.sections)) {
        updated.sections = updated.sections.map((s, i) => i === idx ? { ...s, [field]: value } : s);
      }
    } else if (parts.length === 5 && parts[0] === 'sections' && parts[2] === 'subsections') {
      const si = parseInt(parts[1], 10);
      const ssi = parseInt(parts[3], 10);
      const field = parts[4];
      if (Array.isArray(updated.sections)) {
        updated.sections = updated.sections.map((s, i) => {
          if (i !== si) return s;
          return { ...s, subsections: (s.subsections || []).map((ss, j) => j === ssi ? { ...ss, [field]: value } : ss) };
        });
      }
    } else if (parts.length === 3 && parts[0] === 'ads') {
      const idx = parseInt(parts[1], 10);
      const field = parts[2];
      if (Array.isArray(updated.ads)) {
        updated.ads = updated.ads.map((a, i) => i === idx ? { ...a, [field]: value } : a);
      }
    } else {
      // Nested dot path (hero.headline, problem.content, etc.)
      let obj = updated;
      for (let i = 0; i < parts.length - 1; i++) {
        if (obj[parts[i]] && typeof obj[parts[i]] === 'object') obj = obj[parts[i]];
        else break;
      }
      obj[parts[parts.length - 1]] = value;
    }
  }
  return updated;
}
